import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import isEmpty from "lodash/isEmpty";
import { toast } from "react-toastify";

import HeaderPopup from "./HeaderPopup";
import HeaderLogo from "./HeaderLogo";
import { startFeatureTour } from "./FeatureTour";
import { findTourForPath } from "../../utils/tour/tourSteps";
import AiToolsHeaderPopup from "./AiToolsHeaderPopup";
import { db } from "../../firebase";
import { useAuth } from "../../Contexts/AuthContext";
import {
  getResumeWriterTabColor,
  isWhitelabelFemaleAndFinance,
  getCoverLetterTabColor,
  getServicesTabColor,
  getResumeTabColor,
  getInterviewTabColor,
  getJobsTabColor,
} from "../../helper/common/commonHelper";
import useFetchWlNameLogoComName from "../../hooks/whiteLabel/useFetchWlNameLogoComName";
import useFetchHeaderTabVisibility from "../../hooks/isShowPlan/useFetchHeaderTabVisibility";
import { logFirebaseEvent } from "../../utils/analytics";
import constant from "../../utils/constant";
import accessConstants from "../../utils/accessConstants";
import { isCustomResumeDomain } from "../../utils/functions";

const { HEADER_TAB_ACCESS } = accessConstants;
const { WL, DB_NAME } = constant;

const DoneBadge = () => (
  <span
    className="material-symbols-outlined"
    style={{ fontSize: "16px", color: "#2e7d32" }}
    title="Done"
  >
    check_circle
  </span>
);

const Header = () => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation(); // Get current pathname
  const { currentUser, logout } = useAuth();
  const uid = currentUser?.uid;
  const email = currentUser?.email;
  const whiteLabel = localStorage.getItem("whiteLabel") || "";
  const { wlThemeColor } = useFetchWlNameLogoComName();
  const { isShowHeader } = useFetchHeaderTabVisibility();
  const isWlFemaleAndFinance = isWhitelabelFemaleAndFinance();

  const [page, setPage] = useState("");
  const [pageList, setParentPage] = useState("");
  const [openPopup, setOpenPopup] = useState(false);
  const [progress, setProgress] = useState({
    resume: false,
    coverLetter: false,
    interview: false,
  });

  useEffect(() => {
    const urlArray = pathname.split("/");
    setPage(urlArray[urlArray.length - 1]);
    setParentPage(urlArray);
  }, [pathname]);

  // The two jobs tabs are decided here rather than inline, because `page` is
  // only the LAST path segment - on /jobs it is literally the string "jobs",
  // which is the same sentinel getJobsTabColor treats as "active". Passing
  // `page` as the fallback therefore lit Saved Jobs up on the Find Jobs page
  // (and would light both on any route ending in /jobs). Each tab now answers
  // only for itself, and the inactive branch passes a value that can never
  // collide.
  //
  // /jobs/tailor counts as Find Jobs on purpose: it is reached from there and
  // has no tab of its own.
  const isFindJobsTab = pathname === "/jobs" || pathname.startsWith("/jobs/");
  const isSavedJobsTab = pathname === "/jobs/saved";

  useEffect(() => {
    if (!uid) {
      setProgress({ resume: false, coverLetter: false, interview: false });
      return;
    }

    Promise.all([
      db.collection(DB_NAME.USER_RESUME).doc(uid).get(),
      db.collection(DB_NAME.COVER_LETTER).doc(uid).get(),
      db.collection("interview-qa").doc(uid).get(),
    ])
      .then(([resumeDoc, coverLetterDoc, interviewDoc]) => {
        setProgress({
          resume: !isEmpty(resumeDoc.data()?.others),
          coverLetter: !isEmpty(coverLetterDoc.data()),
          interview: !isEmpty(interviewDoc.data()),
        });
      })
      .catch(() => {});
  }, [uid, pathname]);

  const handleLogout = async () => {
    logFirebaseEvent("LOGOUT_BTN_MAIN_HEADER");
    await logout();
    localStorage.clear();
    localStorage.setItem("whiteLabel", whiteLabel);

    if (whiteLabel) {
      navigate(`/login/?wl=${whiteLabel}`);
    } else {
      navigate("/login");
    }
  };

  const checkClientOnboarded = () => {
    db.collection("onboard-clients")
      .doc("onboard")
      .get()
      .then((doc) => {
        if (doc.data()) {
          const { clients = [] } = doc.data();
          if (!isEmpty(clients)) {
            const values = clients.filter(
              (item) => item?.toLowerCase() === email?.toLowerCase()
            );
            const isClientOnboarded = values.length ? true : false;
            if (isClientOnboarded) {
              navigate("/resume-writer");
            } else {
              navigate("/onboard-resume-writer");
            }
          }
        }
      })
      .catch(() => {
        navigate("/");
      });
  };

  const fetchResumeData = () => {
    db.collection("resume")
      .doc(uid)
      .get()
      .then((doc) => {
        if (!isEmpty(doc.data())) {
          const { others = {} } = doc.data();

          if (!isEmpty(others)) {
            navigate("/create-resume/resume", {
              state: { isResumeGenerateThroughAI: false },
            });
          } else {
            navigate("/select-resume-type");
          }
        } else {
          navigate("/select-resume-type");
        }
      })
      .catch((err) => {
        toast.error("Something went wrong. Please try again.", err);
      });
  };

  const handleClickResume = () => {
    logFirebaseEvent("RESUME_TAB_MAIN_HEADER");
    localStorage.setItem("fromWhere", currentUser ? "" : "resume");

    if (currentUser) {
      fetchResumeData();
    } else {
      navigate("/select-template");
    }
  };

  const handleClickResumeWriter = () => {
    logFirebaseEvent("RESUME_WRITING_STUDIO_TAB_MAIN_HEADER");
    localStorage.setItem("fromWhere", currentUser ? "" : "resume-writer");

    if (currentUser) {
      checkClientOnboarded();
    } else {
      navigate("/login");
    }
  };

  // The logo means "home", not "resume". "/" runs the landing decision: a
  // signed-in user with saved jobs lands on the tracker, one with a resume
  // on Find Jobs, one without on resume creation; signed out sees the hero.
  const handleClickLogo = (e) => {
    e.preventDefault();
    logFirebaseEvent("LOGO_MAIN_HEADER");
    navigate("/");
  };

  const handleClickLogin = () => {
    logFirebaseEvent("LOGIN_BTN_MAIN_HEADER");
    if (whiteLabel) {
      navigate(`/login/?wl=${whiteLabel}`);
    } else {
      navigate("/login");
    }
  };

  const handleClickRegister = () => {
    logFirebaseEvent("SIGNUP_BTN_MAIN_HEADER");

    if (whiteLabel) {
      navigate(`/register/?wl=${whiteLabel}`);
    } else {
      navigate("/register");
    }
  };

  const handleClickCoverLetter = () => {
    logFirebaseEvent("COVER_LETTER_TAB_MAIN_HEADER");

    if (currentUser) {
      navigate("/cover-letter");
    } else {
      navigate("/login");
    }
  };

  const handleClickInterview = () => {
    logFirebaseEvent("INTERVIEW_TAB_MAIN_HEADER");

    if (currentUser) {
      navigate("/interview");
    } else {
      navigate("/login");
    }
  };

  const handleClickJobs = () => {
    logFirebaseEvent("JOBS_TAB_HEADER");
    localStorage.setItem("fromWhere", currentUser ? "" : "jobs");

    if (currentUser) {
      navigate("/jobs");
    } else if (whiteLabel) {
      navigate(`/login/?wl=${whiteLabel}`);
    } else {
      navigate("/login");
    }
  };

  const handleClickSavedJobs = () => {
    logFirebaseEvent("SAVED_JOBS_TAB_HEADER");
    localStorage.setItem("fromWhere", currentUser ? "" : "jobs");

    if (currentUser) {
      navigate("/jobs/saved");
    } else if (whiteLabel) {
      navigate(`/login/?wl=${whiteLabel}`);
    } else {
      navigate("/login");
    }
  };

  const handleClickServices = () => {
    logFirebaseEvent("CARRER_CHOOSING_TAB_MAIN_HEADER");
    navigate("/services");
  };

  const shouldHideLoginButtons =
    search.includes(`?wl=${whiteLabel}`) && pathname.includes("/login");
  const shouldHideSignUpButtons =
    search.includes(`?wl=${whiteLabel}`) && pathname.includes("/register");

  return (
    <>
      <div className="header">
        <nav className="navbar navbar-expand-lg">
          <div className="container-fluid px-0 px-md-3">
            <a
              className="navbar-brand pointer"
              href="/"
              aria-label="CareerClaw home"
              title="Home"
              onClick={(e) => handleClickLogo(e)}
            >
              <div>
                <HeaderLogo />
              </div>
            </a>
            <button
              className="navbar-toggler"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target="#navbarToggle"
              aria-controls="navbarToggle"
              aria-expanded="false"
              aria-label="Toggle navigation"
            >
              <span className="navbar-toggler-icon"></span>
            </button>

            <div className="collapse navbar-collapse" id="navbarToggle">
              <ul className="navbar-nav mx-auto my-3 my-lg-0">
                <li className="nav-item" data-tour="nav-resume">
                  <a
                    role="button"
                    className="nav-link text-bold mx-1 d-flex align-items-center gap-1"
                    onClick={handleClickResume}
                    style={{
                      color: getResumeTabColor(
                        page,
                        isWlFemaleAndFinance,
                        pageList
                      ),
                    }}
                  >
                    Resume
                    {progress.resume && <DoneBadge />}
                  </a>
                </li>

                <li className="nav-item" data-tour="nav-cover-letter">
                  <a
                    role="button"
                    className="nav-link text-bold mx-1 d-flex align-items-center gap-1"
                    onClick={handleClickCoverLetter}
                    style={{
                      color: getCoverLetterTabColor(page, wlThemeColor),
                    }}
                  >
                    Cover Letter
                    {progress.coverLetter && <DoneBadge />}
                  </a>
                </li>

                <li className="nav-item" data-tour="nav-interview">
                  <a
                    role="button"
                    className="nav-link text-bold mx-1 d-flex align-items-center gap-1"
                    onClick={handleClickInterview}
                    style={{
                      color: getInterviewTabColor(page, wlThemeColor),
                    }}
                  >
                    Interview Prep
                    {progress.interview && <DoneBadge />}
                  </a>
                </li>

                <li className="nav-item" data-tour="nav-find-jobs">
                  <a
                    role="button"
                    className="nav-link text-bold mx-1 d-flex align-items-center gap-1"
                    onClick={handleClickJobs}
                    style={{
                      color: getJobsTabColor(
                        isFindJobsTab && !isSavedJobsTab ? "jobs" : "",
                        wlThemeColor
                      ),
                    }}
                  >
                    Find Jobs
                  </a>
                </li>

                {/* The tracker is where a returning user lands once they have
                    a resume and a saved job, so it has to be reachable from
                    every page - not only from the Find Jobs header button. */}
                <li className="nav-item" data-tour="nav-saved-jobs">
                  <a
                    role="button"
                    className="nav-link text-bold mx-1 d-flex align-items-center gap-1"
                    onClick={handleClickSavedJobs}
                    style={{
                      color: getJobsTabColor(
                        isSavedJobsTab ? "jobs" : "",
                        wlThemeColor
                      ),
                    }}
                  >
                    Saved Jobs
                  </a>
                </li>

                {/* {whiteLabel == "null"
                  ? null
                  : (!currentUser ||
                      HEADER_TAB_ACCESS.includes(email) ||
                      isShowHeader?.isShowResumeTab) && (
                      <li className="nav-item">
                        <a
                          className="nav-link text-bold mx-1"
                          role="button"
                          onClick={handleClickServices}
                          style={{
                            color: getServicesTabColor(
                              page,
                              wlThemeColor,
                              pageList
                            ),
                          }}
                        >
                          Premium Services
                        </a>
                      </li>
                    )} */}

                {/* {(!currentUser ||
                  isShowHeader?.isShowAiToolsTab ||
                  HEADER_TAB_ACCESS.includes(email)) &&
                  !isCustomResumeDomain() && (
                    <li className="nav-item">
                      <a role="button" className="nav-link text-bold mx-1">
                        <AiToolsHeaderPopup
                          page={page}
                          pageList={pageList}
                          isShowHeader={isShowHeader}
                          wlThemeColor={wlThemeColor}
                          openPopup={openPopup}
                          setOpenPopup={setOpenPopup}
                          setOpenDropdown={setOpenDropdown}
                        />
                      </a>
                    </li>
                  )} */}

                {currentUser &&
                  (HEADER_TAB_ACCESS.includes(email) || email === WL.ADMIN) && (
                    <li className="nav-item">
                      <a
                        role="button"
                        className="nav-link text-bold mx-1"
                        onClick={handleClickResumeWriter}
                        style={{
                          color: getResumeWriterTabColor(page, wlThemeColor),
                        }}
                      >
                        Resume Writing Studio
                      </a>
                    </li>
                  )}
              </ul>

              {currentUser ? (
                <>
                  {/* Replays the walkthrough for the page the user is on.
                      Shown only where a walkthrough exists - no button, no
                      "nothing here" message on the other pages. */}
                  {findTourForPath(pathname) && (
                    <button
                      type="button"
                      className="tour-header-btn"
                      data-tour="nav-tour"
                      onClick={() => {
                        logFirebaseEvent("SHOW_ME_AROUND_BTN_HEADER");
                        startFeatureTour();
                      }}
                      title="Replay the walkthrough for this page"
                    >
                      <span className="material-symbols-outlined" aria-hidden="true">
                        help
                      </span>
                      Show me around
                    </button>
                  )}
                  <HeaderPopup email={email} handleUserLogout={handleLogout} />
                </>
              ) : (
                <div className="d-flex mb-3 mb-lg-0">
                  {!shouldHideLoginButtons &&
                    !currentUser &&
                    pathname !== "/login" && (
                      <button
                        className="btn btn-outline-primary"
                        type="button"
                        onClick={handleClickLogin}
                        style={{
                          background: wlThemeColor,
                          borderColor: wlThemeColor,
                          color: wlThemeColor ? "white" : "",
                        }}
                      >
                        Login
                      </button>
                    )}
                  {!shouldHideSignUpButtons && pathname !== "/register" && (
                    <button
                      className="btn btn-primary ms-3 ms-xl-4"
                      type="button"
                      onClick={handleClickRegister}
                      style={{
                        background: wlThemeColor,
                        borderColor: wlThemeColor,
                        color: wlThemeColor ? "white" : "",
                      }}
                    >
                      Sign Up
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </nav>
      </div>
    </>
  );
};

export default Header;
